import { makeAutoObservable, runInAction } from 'mobx';
import * as api from '../api/gptClient';
import { normalizeAspectList } from '../utils/aspectRatios';
import { paramOptions } from '../utils/mediaParams';
import {
    migrateUiSettingsFromServer,
    readLocalUiSettings,
    splitSettingsPatch,
    stripLocalUiFromServerSettings,
    writeLocalUiSettings,
} from '../utils/localUiSettings';

const PROVIDER_LABELS = {
    openrouter: 'OpenRouter',
    nanogpt: 'NanoGPT',
};

class ChatStore {
    provider = 'openrouter';
    chats = [];
    folders = [];
    collapsedFolderIds = {};
    messages = [];
    models = [];
    imageModels = [];
    audioModels = [];
    videoModels = [];
    settings = {
        selected_model: null,
        selected_image_model: null,
        selected_audio_model: null,
        selected_video_model: null,
        temperature: 0.7,
        reasoning_effort: null,
        max_tokens: null,
        pdf_parser: 'auto',
        theme: 'system',
        favorite_models: [],
        favorite_image_models: [],
        favorite_audio_models: [],
        favorite_video_models: [],
        usd_rub_rate: 90,
        message_font_size: 15,
        chat_list_font_size: 14,
        button_scale: 1,
        image_preview_size: 480,
        image_model_stats: {},
        aspect_ratios: [
            { id: 'ar-1-1', ratio: '1:1', name: '' },
            { id: 'ar-16-9', ratio: '16:9', name: '' },
            { id: 'ar-9-16', ratio: '9:16', name: '' },
            { id: 'ar-4-3', ratio: '4:3', name: '' },
            { id: 'ar-3-4', ratio: '3:4', name: '' },
        ],
        context_templates: [],
    };
    activeChatId = null;
    mode = 'chat'; // chat | image | audio | video
    contextPanelChatId = null; // id чата для модалки контекста
    loadingChats = false;
    loadingMessages = false;
    loadingOlderMessages = false;
    hasMoreMessages = false;
    messagePageSize = 40;
    streaming = false;
    streamContent = '';
    streamMessageId = null;
    streamError = null;
    abortController = null;
    attachments = []; // local upload queue
    imageDraft = '';
    imageParams = {
        aspectId: null,
        aspectRatio: '1:1',
        resolution: '1K',
        width: 1024,
        height: 1024,
        n: 1,
    };
    audioParams = {
        voice: null,
        response_format: 'mp3',
        speed: 1,
    };
    videoParams = {
        duration: null,
        aspect_ratio: '16:9',
        resolution: null,
        generate_audio: false,
    };
    videoDraft = '';
    audioDraft = '';
    _persistChatTimer = null;
    _bootSeq = 0;
    sidebarCollapsed = false;
    sidebarPinned = false;
    searchQuery = '';
    draft = '';
    stickToBottom = true;
    credits = null; // { total_credits, total_usage, remaining }
    creditsLoading = false;
    creditsError = null;
    _creditsTimer = null;
    _creditsSeq = 0;
    _selectChatSeq = 0;
    _videoPollIds = new Set();

    constructor() {
        makeAutoObservable(
            this,
            {
                _persistChatTimer: false,
                _creditsTimer: false,
                _creditsSeq: false,
                _selectChatSeq: false,
                _bootSeq: false,
                _videoPollIds: false,
            },
            { autoBind: true }
        );
        this.hydrateLocalState();
        const localUi = readLocalUiSettings();
        if (localUi) {
            Object.assign(this.settings, localUi);
        }
    }

    get providerLabel() {
        return PROVIDER_LABELS[this.provider] || 'OpenRouter';
    }

    storageKey(name) {
        if (this.provider === 'nanogpt') return `bananasgpt_nanogpt_${name}`;
        return `bananasgpt_${name}`;
    }

    hydrateLocalState() {
        try {
            this.draft = localStorage.getItem(this.storageKey('draft')) || '';
        } catch (_) {
            this.draft = '';
        }
        try {
            this.sidebarPinned = localStorage.getItem(this.storageKey('sidebar_pinned')) === '1';
            this.sidebarCollapsed = this.sidebarPinned
                ? false
                : localStorage.getItem(this.storageKey('sidebar_collapsed')) === '1';
        } catch (_) {
            this.sidebarPinned = false;
            this.sidebarCollapsed = false;
        }
        try {
            const rawFolders = localStorage.getItem(this.storageKey('collapsed_folders'));
            const parsedFolders = rawFolders ? JSON.parse(rawFolders) : {};
            this.collapsedFolderIds =
                parsedFolders && typeof parsedFolders === 'object' && !Array.isArray(parsedFolders)
                    ? parsedFolders
                    : {};
        } catch (_) {
            this.collapsedFolderIds = {};
        }
    }

    resetConversationState() {
        this.chats = [];
        this.folders = [];
        this.messages = [];
        this.models = [];
        this.imageModels = [];
        this.audioModels = [];
        this.videoModels = [];
        this.activeChatId = null;
        this.contextPanelChatId = null;
        this.streaming = false;
        this.streamContent = '';
        this.streamMessageId = null;
        this.streamError = null;
        this.abortController = null;
        this.attachments = [];
        this.credits = null;
        this.creditsError = null;
        this.creditsLoading = false;
        this.searchQuery = '';
        this.hasMoreMessages = false;
        this.loadingMessages = false;
        this.loadingOlderMessages = false;
        this.loadingChats = false;
    }

    setProvider(next) {
        const provider = next === 'nanogpt' ? 'nanogpt' : 'openrouter';
        api.setGptProvider(provider);
        this._creditsSeq += 1;
        if (this.provider === provider) return;
        try {
            this.abortController?.abort();
        } catch (_) {
            /* ignore */
        }
        this.provider = provider;
        this.resetConversationState();
        this.hydrateLocalState();
    }

    get activeChat() {
        return this.chats.find((c) => c.id === this.activeChatId) || null;
    }

    get filteredChats() {
        const q = this.searchQuery.trim().toLowerCase();
        if (!q) return this.chats;
        return this.chats.filter((c) => (c.title || '').toLowerCase().includes(q));
    }

    get pinnedChats() {
        return this.filteredChats.filter((c) => c.is_pinned);
    }

    get unpinnedChats() {
        return this.filteredChats.filter((c) => !c.is_pinned);
    }

    chatsInFolder(folderId) {
        const id = folderId || null;
        return this.unpinnedChats.filter((c) => (c.folder_id || null) === id);
    }

    get rootChats() {
        return this.chatsInFolder(null);
    }

    get selectedModelInfo() {
        const id = this.settings.selected_model;
        return this.models.find((m) => m.id === id) || null;
    }

    setDraft(value) {
        this.draft = value;
        try {
            localStorage.setItem(this.storageKey('draft'), value || '');
        } catch (_) {
            /* ignore */
        }
    }

    setImageDraft(value) {
        this.imageDraft = value || '';
    }

    setSearchQuery(value) {
        this.searchQuery = value;
    }

    setSidebarCollapsed(value) {
        // Закреплённую панель нельзя свернуть, пока не открепят
        if (value && this.sidebarPinned) return;
        this.sidebarCollapsed = !!value;
        try {
            localStorage.setItem(this.storageKey('sidebar_collapsed'), this.sidebarCollapsed ? '1' : '0');
        } catch (_) {
            /* ignore */
        }
    }

    setSidebarPinned(value) {
        this.sidebarPinned = !!value;
        if (this.sidebarPinned) this.sidebarCollapsed = false;
        try {
            localStorage.setItem(this.storageKey('sidebar_pinned'), this.sidebarPinned ? '1' : '0');
            localStorage.setItem(this.storageKey('sidebar_collapsed'), this.sidebarCollapsed ? '1' : '0');
        } catch (_) {
            /* ignore */
        }
    }

    toggleSidebarPinned() {
        this.setSidebarPinned(!this.sidebarPinned);
    }

    /** После выбора/создания чата сворачиваем боковую панель (если не закреплена) */
    collapseSidebarAfterChatSelect() {
        if (this.sidebarPinned) return;
        this.setSidebarCollapsed(true);
    }

    /** После первого сообщения тип диалога (чат / изображение) фиксируется */
    get isModeLocked() {
        return this.messages.length > 0;
    }

    setMode(mode, { force = false } = {}) {
        const allowed = ['chat', 'image', 'audio', 'video'];
        const next = allowed.includes(mode) ? mode : 'chat';
        if (!force && this.isModeLocked && this.mode !== next) return;
        this.mode = next;
        this.schedulePersistChatParams();
    }

    get showMediaTabs() {
        return this.provider === 'nanogpt' || this.audioModels.length > 0 || this.videoModels.length > 0;
    }

    setStickToBottom(value) {
        this.stickToBottom = value;
    }

    buildChatParams() {
        return {
            mode: this.mode,
            selected_model: this.settings.selected_model,
            selected_image_model: this.settings.selected_image_model,
            selected_audio_model: this.settings.selected_audio_model,
            selected_video_model: this.settings.selected_video_model,
            temperature: this.settings.temperature,
            reasoning_effort: this.settings.reasoning_effort,
            image: {
                aspect_id: this.imageParams.aspectId,
                aspect_ratio: this.imageParams.aspectRatio,
                resolution: this.imageParams.resolution,
                width: this.imageParams.width,
                height: this.imageParams.height,
                n: this.imageParams.n,
            },
            audio: { ...this.audioParams },
            video: { ...this.videoParams },
        };
    }

    applyChatParams(chat) {
        if (!chat) return;
        const p = chat.params && typeof chat.params === 'object' ? chat.params : {};
        const mode = p.mode || chat.mode || this.mode || 'chat';
        this.setMode(mode, { force: true });

        if (p.selected_model) this.settings.selected_model = p.selected_model;
        else if (chat.model && this.mode === 'chat') this.settings.selected_model = chat.model;

        if (p.selected_image_model) this.settings.selected_image_model = p.selected_image_model;
        else if (chat.model && this.mode === 'image') this.settings.selected_image_model = chat.model;

        if (p.selected_audio_model) this.settings.selected_audio_model = p.selected_audio_model;
        else if (chat.model && this.mode === 'audio') this.settings.selected_audio_model = chat.model;

        if (p.selected_video_model) this.settings.selected_video_model = p.selected_video_model;
        else if (chat.model && this.mode === 'video') this.settings.selected_video_model = chat.model;

        if (p.temperature != null && Number.isFinite(Number(p.temperature))) {
            this.settings.temperature = Number(p.temperature);
        }
        if (p.reasoning_effort != null && p.reasoning_effort !== '') {
            this.settings.reasoning_effort = String(p.reasoning_effort);
        }

        const img = p.image && typeof p.image === 'object' ? p.image : {};
        this.imageParams = {
            aspectId: img.aspect_id || this.imageParams.aspectId,
            aspectRatio: img.aspect_ratio || this.imageParams.aspectRatio || '1:1',
            resolution: img.resolution || this.imageParams.resolution || '1K',
            width: Number(img.width) || this.imageParams.width || 1024,
            height: Number(img.height) || this.imageParams.height || 1024,
            n: Math.max(1, Number(img.n) || this.imageParams.n || 1),
        };

        const audio = p.audio && typeof p.audio === 'object' ? p.audio : {};
        this.audioParams = {
            voice: audio.voice || this.audioParams.voice,
            response_format: audio.response_format || this.audioParams.response_format || 'mp3',
            speed: Number(audio.speed) || this.audioParams.speed || 1,
        };
        const video = p.video && typeof p.video === 'object' ? p.video : {};
        this.videoParams = {
            duration: video.duration || this.videoParams.duration,
            aspect_ratio: video.aspect_ratio || this.videoParams.aspect_ratio || '16:9',
            resolution: video.resolution || this.videoParams.resolution,
            generate_audio: Boolean(video.generate_audio),
        };
    }

    setAudioParams(patch = {}, { persist = true } = {}) {
        this.audioParams = { ...this.audioParams, ...patch };
        if (persist) this.schedulePersistChatParams();
    }

    setVideoParams(patch = {}, { persist = true } = {}) {
        this.videoParams = { ...this.videoParams, ...patch };
        if (persist) this.schedulePersistChatParams();
    }

    setAudioDraft(value) {
        this.audioDraft = value;
    }

    setVideoDraft(value) {
        this.videoDraft = value;
    }

    setImageParams(patch = {}, { persist = true } = {}) {
        this.imageParams = { ...this.imageParams, ...patch };
        if (persist) this.schedulePersistChatParams();
    }

    schedulePersistChatParams() {
        if (this._persistChatTimer) clearTimeout(this._persistChatTimer);
        this._persistChatTimer = setTimeout(() => {
            this.persistChatParams().catch(() => {});
        }, 400);
    }

    async persistChatParams() {
        const chatId = this.activeChatId;
        if (!chatId) return null;
        const params = this.buildChatParams();
        const model =
            this.mode === 'image'
                ? this.settings.selected_image_model
                : this.mode === 'audio'
                  ? this.settings.selected_audio_model
                  : this.mode === 'video'
                    ? this.settings.selected_video_model
                    : this.settings.selected_model;
        try {
            const updated = await api.updateChat(chatId, {
                mode: this.mode,
                model: model || undefined,
                params,
            });
            runInAction(() => {
                this.chats = this.chats.map((c) => (c.id === chatId ? { ...c, ...updated } : c));
            });
            return updated;
        } catch (_) {
            return null;
        }
    }

    async bootstrap() {
        const seq = ++this._bootSeq;
        await Promise.all([this.loadSettings(), this.loadChats(), this.loadFolders()]);
        if (seq !== this._bootSeq) return;
        try {
            await this.loadModels();
        } catch (_) {
            /* models require provider API key */
        }
        if (seq !== this._bootSeq) return;
        await Promise.all([
            this.loadImageModels().catch(() => {}),
            this.loadAudioModels().catch(() => {}),
            this.loadVideoModels().catch(() => {}),
        ]);
        if (seq !== this._bootSeq) return;
        this.loadCredits();
    }

    async loadCredits({ force = false } = {}) {
        if (this.creditsLoading && !force) return;
        const provider = this.provider;
        const seq = ++this._creditsSeq;
        this.creditsLoading = true;
        this.creditsError = null;
        try {
            const data = await api.fetchCredits(provider);
            if (seq !== this._creditsSeq || this.provider !== provider) return;
            runInAction(() => {
                this.credits = {
                    total_credits: Number(data.total_credits) || 0,
                    total_usage: Number(data.total_usage) || 0,
                    remaining: Number(data.remaining) || 0,
                };
                this.creditsLoading = false;
            });
        } catch (e) {
            if (seq !== this._creditsSeq || this.provider !== provider) return;
            runInAction(() => {
                this.creditsError = e.gptError?.message || e.message || 'Ошибка загрузки баланса';
                this.creditsLoading = false;
            });
        }
    }

    async loadSettings() {
        const data = await api.fetchSettings();
        // тема/шрифты/масштаб — из браузера (с одноразовой миграцией с сервера)
        const localUi = migrateUiSettingsFromServer(data);
        const remote = stripLocalUiFromServerSettings(data || {});
        runInAction(() => {
            this.settings = {
                ...this.settings,
                ...remote,
                ...localUi,
                aspect_ratios: normalizeAspectList(data.aspect_ratios ?? this.settings.aspect_ratios),
                context_templates: Array.isArray(data.context_templates)
                    ? data.context_templates
                    : this.settings.context_templates || [],
                image_model_stats:
                    data.image_model_stats && typeof data.image_model_stats === 'object'
                        ? data.image_model_stats
                        : this.settings.image_model_stats || {},
            };
        });
    }

    async saveSettings(patch) {
        const { localPatch, remotePatch } = splitSettingsPatch(patch || {});
        let merged = { ...this.settings };

        if (Object.keys(localPatch).length) {
            const localUi = writeLocalUiSettings(localPatch);
            merged = { ...merged, ...localUi };
            runInAction(() => {
                this.settings = { ...this.settings, ...localUi };
            });
        }

        if (Object.keys(remotePatch).length) {
            const data = await api.patchSettings(remotePatch);
            const remote = stripLocalUiFromServerSettings(data || {});
            runInAction(() => {
                this.settings = {
                    ...this.settings,
                    ...remote,
                    // локальный UI не перетираем ответом сервера
                    ...(readLocalUiSettings() || {}),
                    aspect_ratios: normalizeAspectList(
                        data.aspect_ratios ?? this.settings.aspect_ratios
                    ),
                    context_templates: Array.isArray(data.context_templates)
                        ? data.context_templates
                        : this.settings.context_templates || [],
                    image_model_stats:
                        data.image_model_stats && typeof data.image_model_stats === 'object'
                            ? data.image_model_stats
                            : this.settings.image_model_stats || {},
                };
                merged = this.settings;
            });
            if (
                'selected_model' in remotePatch ||
                'selected_image_model' in remotePatch ||
                'selected_audio_model' in remotePatch ||
                'selected_video_model' in remotePatch ||
                'temperature' in remotePatch ||
                'reasoning_effort' in remotePatch
            ) {
                this.schedulePersistChatParams();
            }
        }

        return merged;
    }

    openContextPanel(chatId) {
        this.contextPanelChatId = chatId || this.activeChatId;
    }

    closeContextPanel() {
        this.contextPanelChatId = null;
    }

    async saveChatContext(chatId, messages) {
        const id = chatId || this.activeChatId;
        if (!id) return null;
        const cleaned = (messages || [])
            .map((m) => ({
                role: String(m.role || '').trim(),
                content: String(m.content || '').trim(),
            }))
            .filter((m) => m.role && m.content);
        const updated = await api.updateChat(id, { context_messages: cleaned });
        runInAction(() => {
            this.chats = this.chats.map((c) => (c.id === id ? { ...c, ...updated } : c));
        });
        return updated;
    }

    async saveContextTemplate(name, messages) {
        const title = String(name || '').trim();
        if (!title) throw new Error('Укажите название шаблона');
        const cleaned = (messages || [])
            .map((m) => ({
                role: String(m.role || '').trim(),
                content: String(m.content || '').trim(),
            }))
            .filter((m) => m.role && m.content);
        const templates = [...(this.settings.context_templates || [])];
        const id =
            typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : `tpl-${Date.now()}`;
        templates.push({ id, name: title, messages: cleaned });
        await this.saveSettings({ context_templates: templates });
        return id;
    }

    async deleteContextTemplate(templateId) {
        const templates = (this.settings.context_templates || []).filter((t) => t.id !== templateId);
        await this.saveSettings({ context_templates: templates });
    }

    async applyContextTemplate(chatId, templateId) {
        const tpl = (this.settings.context_templates || []).find((t) => t.id === templateId);
        if (!tpl) throw new Error('Шаблон не найден');
        return this.saveChatContext(chatId, tpl.messages || []);
    }

    async toggleFavoriteModel(modelId, imageMode = false) {
        if (!modelId) return;
        const kind = typeof imageMode === 'string' ? imageMode : imageMode ? 'image' : 'chat';
        const key =
            kind === 'image'
                ? 'favorite_image_models'
                : kind === 'audio'
                  ? 'favorite_audio_models'
                  : kind === 'video'
                    ? 'favorite_video_models'
                    : 'favorite_models';
        const current = [...(this.settings[key] || [])];
        const next = current.includes(modelId)
            ? current.filter((id) => id !== modelId)
            : [modelId, ...current.filter((id) => id !== modelId)];
        runInAction(() => {
            this.settings[key] = next;
        });
        try {
            await this.saveSettings({ [key]: next });
        } catch (e) {
            runInAction(() => {
                this.settings[key] = current;
            });
            throw e;
        }
    }

    async loadModels() {
        const data = await api.fetchModels();
        runInAction(() => {
            this.models = data || [];
            if (!this.settings.selected_model && this.models.length) {
                this.settings.selected_model = this.models[0].id;
            }
        });
    }

    async loadImageModels() {
        const data = await api.fetchImageModels();
        runInAction(() => {
            this.imageModels = data || [];
            if (!this.settings.selected_image_model && this.imageModels.length) {
                this.settings.selected_image_model = this.imageModels[0].id;
            }
        });
    }

    async loadAudioModels() {
        const data = await api.fetchAudioModels();
        const models = Array.isArray(data) ? data : [];
        runInAction(() => {
            this.audioModels = models;
            if (!this.settings.selected_audio_model && this.audioModels.length) {
                this.settings.selected_audio_model = this.audioModels[0].id;
            }
        });
        return models;
    }

    async loadVideoModels() {
        const data = await api.fetchVideoModels();
        const models = Array.isArray(data) ? data : [];
        runInAction(() => {
            this.videoModels = models;
            if (!this.settings.selected_video_model && this.videoModels.length) {
                this.settings.selected_video_model = this.videoModels[0].id;
            }
        });
        return models;
    }

    async loadChats() {
        this.loadingChats = true;
        try {
            const data = await api.fetchChats();
            runInAction(() => {
                this.chats = data || [];
            });
        } finally {
            runInAction(() => {
                this.loadingChats = false;
            });
        }
    }

    async loadFolders() {
        try {
            const data = await api.fetchFolders();
            runInAction(() => {
                this.folders = data || [];
            });
        } catch (_) {
            runInAction(() => {
                this.folders = [];
            });
        }
    }

    toggleFolderCollapsed(folderId) {
        this.collapsedFolderIds = {
            ...this.collapsedFolderIds,
            [folderId]: !this.collapsedFolderIds[folderId],
        };
        try {
            localStorage.setItem(this.storageKey('collapsed_folders'), JSON.stringify(this.collapsedFolderIds));
        } catch (_) {
            /* ignore */
        }
    }

    async createFolder(name) {
        const folder = await api.createFolder({ name: name || 'Новая папка' });
        runInAction(() => {
            this.folders = [...this.folders, folder].sort((a, b) =>
                (a.name || '').localeCompare(b.name || '', 'ru')
            );
        });
        return folder;
    }

    async renameFolder(folderId, name) {
        const updated = await api.updateFolder(folderId, { name });
        runInAction(() => {
            this.folders = this.folders
                .map((f) => (f.id === folderId ? updated : f))
                .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ru'));
        });
        return updated;
    }

    async removeFolder(folderId) {
        await api.deleteFolder(folderId);
        runInAction(() => {
            this.folders = this.folders.filter((f) => f.id !== folderId);
            this.chats = this.chats.map((c) =>
                c.folder_id === folderId ? { ...c, folder_id: null } : c
            );
        });
    }

    async togglePinChat(chatId) {
        const chat = this.chats.find((c) => c.id === chatId);
        if (!chat) return;
        const updated = await api.updateChat(chatId, { is_pinned: !chat.is_pinned });
        runInAction(() => {
            this.chats = this.chats.map((c) => (c.id === chatId ? updated : c));
            // keep pinned first ordering
            this.chats = [...this.chats].sort((a, b) => {
                if (!!a.is_pinned !== !!b.is_pinned) return a.is_pinned ? -1 : 1;
                return 0;
            });
        });
        return updated;
    }

    async moveChatToFolder(chatId, folderId) {
        const updated = await api.updateChat(chatId, {
            folder_id: folderId || null,
        });
        runInAction(() => {
            this.chats = this.chats.map((c) => (c.id === chatId ? updated : c));
        });
        return updated;
    }

    async selectChat(chatId) {
        const prevId = this.activeChatId;
        // не блокируем UI: параметры прошлого чата сохраняем в фоне
        if (prevId && prevId !== chatId) {
            this.persistChatParams().catch(() => {});
        }

        const seq = ++this._selectChatSeq;

        runInAction(() => {
            this.activeChatId = chatId;
            this.streamError = null;
            this.messages = [];
            this.stickToBottom = true;
            this.loadingMessages = !!chatId;
            this.loadingOlderMessages = false;
            this.hasMoreMessages = false;
            if (chatId) {
                const chat = this.chats.find((c) => c.id === chatId);
                if (chat) this.applyChatParams(chat);
            }
            this.collapseSidebarAfterChatSelect();
        });

        if (!chatId) return;

        try {
            const data = await api.fetchMessages(chatId, {
                limit: this.messagePageSize,
            });
            if (seq !== this._selectChatSeq || this.activeChatId !== chatId) return;
            runInAction(() => {
                this.messages = data?.messages || [];
                this.hasMoreMessages = !!data?.has_more;
                this.stickToBottom = true;
                const fresh = this.chats.find((c) => c.id === chatId);
                if (fresh) this.applyChatParams(fresh);
            });
            const pendingVideo = (data?.messages || []).find((m) => m.status === 'generating_video');
            if (pendingVideo?.id) this.pollVideoStatus(pendingVideo.id, chatId);
        } catch (e) {
            if (seq !== this._selectChatSeq || this.activeChatId !== chatId) return;
            runInAction(() => {
                this.streamError = e.gptError || {
                    code: 'DB_ERROR',
                    message: e.message || 'Не удалось загрузить сообщения',
                };
            });
        } finally {
            if (seq === this._selectChatSeq) {
                runInAction(() => {
                    this.loadingMessages = false;
                });
            }
        }
    }

    async loadOlderMessages() {
        const chatId = this.activeChatId;
        const before = this.messages[0]?.id;
        if (!chatId || !before || !this.hasMoreMessages || this.loadingOlderMessages) {
            return 0;
        }

        this.loadingOlderMessages = true;
        try {
            const data = await api.fetchMessages(chatId, {
                limit: this.messagePageSize,
                before,
            });
            if (this.activeChatId !== chatId) return 0;
            const older = data?.messages || [];
            const currentIds = new Set(this.messages.map((message) => message.id));
            const uniqueOlder = older.filter((message) => !currentIds.has(message.id));
            runInAction(() => {
                this.messages = [...uniqueOlder, ...this.messages];
                this.hasMoreMessages = !!data?.has_more;
            });
            return uniqueOlder.length;
        } catch (e) {
            runInAction(() => {
                this.streamError = e.gptError || {
                    code: 'DB_ERROR',
                    message: e.message || 'Не удалось загрузить ранние сообщения',
                };
            });
            return 0;
        } finally {
            runInAction(() => {
                this.loadingOlderMessages = false;
            });
        }
    }

    async newChat() {
        const params = this.buildChatParams();
        const chat = await api.createChat({
            title: 'Новый чат',
            model:
                this.mode === 'image'
                    ? this.settings.selected_image_model
                    : this.mode === 'audio'
                      ? this.settings.selected_audio_model
                      : this.mode === 'video'
                        ? this.settings.selected_video_model
                        : this.settings.selected_model,
            mode: this.mode,
            params,
        });
        runInAction(() => {
            this.chats = [chat, ...this.chats];
            this.activeChatId = chat.id;
            this.messages = [];
            this.hasMoreMessages = false;
            this.loadingOlderMessages = false;
            this.streamError = null;
            this.applyChatParams(chat);
            this.collapseSidebarAfterChatSelect();
        });
        return chat;
    }

    async renameChat(chatId, title) {
        const updated = await api.updateChat(chatId, { title });
        runInAction(() => {
            this.chats = this.chats.map((c) => (c.id === chatId ? updated : c));
        });
    }

    async deleteMessage(messageId) {
        const chatId = this.activeChatId;
        if (!chatId || !messageId) return;
        if (String(messageId).startsWith('temp-')) {
            runInAction(() => {
                this.messages = this.messages.filter((m) => m.id !== messageId);
            });
            return;
        }
        const data = await api.deleteMessage(chatId, messageId);
        const deleted = new Set(data?.deleted_ids || [messageId]);
        runInAction(() => {
            this.messages = this.messages.filter((m) => !deleted.has(m.id));
        });
    }

    async removeChat(chatId) {
        await api.deleteChat(chatId);
        runInAction(() => {
            this.chats = this.chats.filter((c) => c.id !== chatId);
            if (this.activeChatId === chatId) {
                this.activeChatId = null;
                this.messages = [];
            }
        });
    }

    addAttachmentLocal(item) {
        this.attachments.push(item);
    }

    updateAttachment(localId, patch) {
        this.attachments = this.attachments.map((a) =>
            a.localId === localId ? { ...a, ...patch } : a
        );
    }

    removeAttachment(localId) {
        const item = this.attachments.find((a) => a.localId === localId);
        if (item?.abort) item.abort.abort();
        this.attachments = this.attachments.filter((a) => a.localId !== localId);
    }

    clearReadyAttachments() {
        this.attachments = this.attachments.filter((a) => a.status !== 'ready');
    }

    /** Прикрепить уже загруженный файл (recreate) — скачиваем blob и грузим заново с прогрессом */
    async attachExistingFile(meta = {}) {
        const fileId = meta.file_id || meta.fileId;
        const url = meta.url || (fileId ? api.fileDownloadUrl(fileId) : null);
        if (!url && !fileId) return null;

        const full = (url || '').startsWith('http') ? url : api.fileDownloadUrl(fileId);
        const token = localStorage.getItem('token');
        const res = await fetch(full, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
            throw new Error(meta.name ? `Не удалось загрузить ${meta.name}` : 'Не удалось загрузить файл');
        }
        const blob = await res.blob();
        const mime = blob.type || meta.mime || meta.mime_type || 'image/png';
        const ext = (mime.split('/')[1] || 'bin').replace('jpeg', 'jpg');
        const name = meta.name || meta.original_name || `file-${fileId || Date.now()}.${ext}`;
        const file = new File([blob], name, { type: mime });
        await this.uploadAttachment(file);
        return this.attachments[this.attachments.length - 1] || null;
    }

    /** Вставить изображение как референс в следующий промпт (скачать и загрузить заново) */
    async useImageAsReference(image) {
        if (!image?.file_id && !image?.url) return;
        this.setMode('image');

        const mime = image.mime || 'image/png';
        const ext = (mime.split('/')[1] || 'png').replace('jpeg', 'jpg');
        const name = `reference-${image.file_id || Date.now()}.${ext}`;
        const url = image.url || api.fileDownloadUrl(image.file_id);
        const full = url.startsWith('http') ? url : api.fileDownloadUrl(image.file_id);

        const token = localStorage.getItem('token');
        const res = await fetch(full, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
            throw new Error('Не удалось загрузить изображение для референса');
        }
        const blob = await res.blob();
        const type = blob.type || mime;
        if (!type.startsWith('image/')) {
            throw new Error('Референс должен быть изображением');
        }
        const file = new File([blob], name, { type });
        await this.uploadAttachment(file);
    }

    /** Подставить исходный промпт и файлы генерации */
    async recreateImageGeneration(assistantMessage) {
        this.setMode('image');

        let prompt = assistantMessage?.source_prompt || '';
        let files = assistantMessage?.source_attachments || [];

        if (!prompt || !files.length) {
            const idx = this.messages.findIndex((m) => m.id === assistantMessage?.id);
            for (let i = idx - 1; i >= 0; i -= 1) {
                const m = this.messages[i];
                if (m.role === 'user') {
                    if (!prompt) prompt = m.content || '';
                    if (!files.length) files = m.attachments || [];
                    break;
                }
            }
        }

        this.setImageDraft(prompt || '');
        // очищаем текущие ready-вложения и ставим исходные
        const keepUploading = this.attachments.filter((a) => a.status !== 'ready');
        runInAction(() => {
            this.attachments = keepUploading;
        });
        for (const f of files) {
            // eslint-disable-next-line no-await-in-loop
            await this.attachExistingFile(f);
        }
    }

    /** Подставить текст и вложения исходящего сообщения в композер */
    async recreateUserMessage(message) {
        const prompt = message?.content || '';
        const files = message?.attachments || [];
        if (this.isModeLocked) {
            if (this.mode === 'image') this.setImageDraft(prompt);
            else if (this.mode === 'audio') this.setAudioDraft(prompt);
            else if (this.mode === 'video') this.setVideoDraft(prompt);
            else this.setDraft(prompt);
        } else {
            const kindOf = (a) => `${a.kind || ''} ${a.mime || ''} ${a.mime_type || ''}`.toLowerCase();
            const hasImage = files.some((a) => kindOf(a).includes('image'));
            const hasAudio = files.some((a) => kindOf(a).includes('audio'));
            const hasVideo = files.some((a) => kindOf(a).includes('video'));
            if (this.mode === 'image' || hasImage) {
                this.setMode('image');
                this.setImageDraft(prompt);
            } else if (this.mode === 'audio' || hasAudio) {
                this.setMode('audio');
                this.setAudioDraft(prompt);
            } else if (this.mode === 'video' || hasVideo) {
                this.setMode('video');
                this.setVideoDraft(prompt);
            } else {
                this.setMode('chat');
                this.setDraft(prompt);
            }
        }

        const keepUploading = this.attachments.filter((a) => a.status !== 'ready');
        runInAction(() => {
            this.attachments = keepUploading;
        });
        for (const f of files) {
            // eslint-disable-next-line no-await-in-loop
            await this.attachExistingFile(f);
        }
    }

    async uploadAttachment(file) {
        const localId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const abort = new AbortController();
        const previewUrl = file.type?.startsWith('image/') ? URL.createObjectURL(file) : null;
        this.addAttachmentLocal({
            localId,
            file,
            name: file.name,
            size: file.size,
            mime: file.type,
            status: 'queued',
            percent: 0,
            loaded: 0,
            total: file.size,
            previewUrl,
            fileId: null,
            url: null,
            error: null,
            abort,
        });

        this.updateAttachment(localId, { status: 'uploading' });
        try {
            const uploaded = await api.uploadFile(file, {
                chatId: this.activeChatId,
                signal: abort.signal,
                onProgress: ({ loaded, total, percent }) => {
                    this.updateAttachment(localId, {
                        loaded,
                        total,
                        percent,
                        status: 'uploading',
                    });
                },
            });
            this.updateAttachment(localId, {
                status: 'ready',
                percent: 100,
                loaded: file.size,
                fileId: uploaded.id,
                url: uploaded.url,
                kind: uploaded.kind || (file.type?.startsWith('image/') ? 'image' : 'other'),
            });
        } catch (e) {
            if (abort.signal.aborted) {
                this.updateAttachment(localId, { status: 'cancelled', error: 'Отменено' });
            } else {
                this.updateAttachment(localId, {
                    status: 'error',
                    error: e.gptError?.message || e.message || 'Ошибка загрузки',
                });
                this.streamError = e.gptError || {
                    code: 'UPLOAD_ERROR',
                    message: e.gptError?.message || e.message || 'Ошибка загрузки файла',
                };
            }
        }
    }

    async sendMessage() {
        const text = (this.draft || '').trim();
        const readyFiles = this.attachments.filter((a) => a.status === 'ready' && a.fileId);
        if (!text && !readyFiles.length) return;
        if (this.streaming) return;

        const model = this.settings.selected_model;
        if (!model) {
            this.streamError = { code: 'MODEL_NOT_FOUND', message: 'Выберите модель' };
            return;
        }

        const modelInfo = this.selectedModelInfo;
        const hasImage = readyFiles.some((f) => (f.kind || f.mime || '').includes('image'));
        if (hasImage && modelInfo && !(modelInfo.input_modalities || []).includes('image')) {
            this.streamError = {
                code: 'MODEL_NOT_SUPPORTED',
                message: 'Выбранная модель не поддерживает изображения. Выберите vision-модель.',
            };
            return;
        }
        const hasAudio = readyFiles.some(
            (f) => (f.kind || '').includes('audio') || (f.mime || '').startsWith('audio/')
        );
        if (hasAudio && modelInfo && !(modelInfo.input_modalities || []).includes('audio')) {
            this.streamError = {
                code: 'MODEL_NOT_SUPPORTED',
                message: 'Выбранная модель не поддерживает аудио. Выберите модель с audio input.',
            };
            return;
        }

        let chatId = this.activeChatId;
        if (!chatId) {
            const chat = await this.newChat();
            chatId = chat.id;
        }

        const payload = {
            content: text,
            file_ids: readyFiles.map((f) => f.fileId),
            model,
            temperature: this.settings.temperature,
            reasoning_effort: this.settings.reasoning_effort || undefined,
            max_tokens: this.settings.max_tokens,
            pdf_parser: this.settings.pdf_parser,
        };

        this.setDraft('');
        this.attachments = [];
        this.streaming = true;
        this.streamContent = '';
        this.streamError = null;
        this.stickToBottom = true;
        this.abortController = new AbortController();

        const tempUser = {
            id: `temp-user-${Date.now()}`,
            chat_id: chatId,
            role: 'user',
            content: text,
            attachments: readyFiles.map((f) => ({
                file_id: f.fileId,
                kind: f.kind || 'other',
                name: f.name,
                mime: f.mime,
                size: f.size,
                url: f.url,
            })),
            status: 'complete',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };
        const tempAssistant = {
            id: `temp-asst-${Date.now()}`,
            chat_id: chatId,
            role: 'assistant',
            content: '',
            status: 'streaming',
            model,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };
        this.messages = [...this.messages, tempUser, tempAssistant];

        try {
            await api.streamChatMessage(chatId, payload, {
                signal: this.abortController.signal,
                onEvent: ({ event, data }) => this.handleStreamEvent(event, data, tempAssistant.id),
            });
            await this.loadChats();
            await this.selectChat(chatId);
        } catch (e) {
            if (e.name === 'AbortError') {
                runInAction(() => {
                    this.streamError = null;
                });
            } else {
                runInAction(() => {
                    this.streamError = e.gptError || {
                        code: 'PROVIDER_ERROR',
                        message: e.message || 'Ошибка генерации',
                    };
                    this.messages = this.messages.map((m) =>
                        m.id === tempAssistant.id
                            ? { ...m, status: 'error', error: this.streamError, content: this.streamContent }
                            : m
                    );
                });
            }
        } finally {
            runInAction(() => {
                this.streaming = false;
                this.abortController = null;
            });
            this.loadCredits();
        }
    }

    handleStreamEvent(event, data, tempAssistantId) {
        if (event === 'metadata') {
            if (data?.assistant_message_id) {
                this.streamMessageId = data.assistant_message_id;
                this.messages = this.messages.map((m) =>
                    m.id === tempAssistantId ? { ...m, id: data.assistant_message_id, model: data.model || m.model } : m
                );
            }
            return;
        }
        if (event === 'delta') {
            this.streamContent += data?.content || '';
            const id = this.streamMessageId || tempAssistantId;
            this.messages = this.messages.map((m) =>
                m.id === id ? { ...m, content: this.streamContent, status: 'streaming' } : m
            );
            return;
        }
        if (event === 'usage') {
            const id = this.streamMessageId || tempAssistantId;
            this.messages = this.messages.map((m) =>
                m.id === id ? { ...m, usage: data } : m
            );
            return;
        }
        if (event === 'error') {
            this.streamError = data;
            const id = this.streamMessageId || tempAssistantId;
            this.messages = this.messages.map((m) =>
                m.id === id
                    ? {
                          ...m,
                          status: data?.code === 'CANCELLED' ? 'cancelled' : 'error',
                          error: data,
                          content: this.streamContent,
                      }
                    : m
            );
            return;
        }
        if (event === 'done') {
            const id = data?.assistant_message_id || this.streamMessageId || tempAssistantId;
            this.messages = this.messages.map((m) =>
                m.id === id || m.id === tempAssistantId
                    ? {
                          ...m,
                          id,
                          content: this.streamContent || m.content,
                          status: 'complete',
                          usage: data?.usage || m.usage,
                          annotations: data?.annotations || m.annotations,
                      }
                    : m
            );
        }
    }

    async stop() {
        if (!this.streaming) return;
        const chatId = this.activeChatId;
        const messageId = this.streamMessageId;
        try {
            this.abortController?.abort();
        } catch (_) {
            /* ignore */
        }
        if (chatId) {
            try {
                await api.stopGeneration(chatId, messageId);
            } catch (_) {
                /* ignore */
            }
        }
        runInAction(() => {
            this.streaming = false;
        });
    }

    async regenerate() {
        if (!this.activeChatId || this.streaming) return;
        this.streaming = true;
        this.streamContent = '';
        this.streamError = null;
        this.abortController = new AbortController();

        // remove last assistant locally
        const msgs = [...this.messages];
        while (msgs.length && msgs[msgs.length - 1].role === 'assistant') {
            msgs.pop();
        }
        const tempAssistant = {
            id: `temp-asst-${Date.now()}`,
            chat_id: this.activeChatId,
            role: 'assistant',
            content: '',
            status: 'streaming',
            model: this.settings.selected_model,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };
        this.messages = [...msgs, tempAssistant];

        try {
            await api.streamRegenerate(
                this.activeChatId,
                {
                    model: this.settings.selected_model,
                    temperature: this.settings.temperature,
                    reasoning_effort: this.settings.reasoning_effort || undefined,
                },
                {
                    signal: this.abortController.signal,
                    onEvent: ({ event, data }) => this.handleStreamEvent(event, data, tempAssistant.id),
                }
            );
            await this.selectChat(this.activeChatId);
        } catch (e) {
            if (e.name !== 'AbortError') {
                runInAction(() => {
                    this.streamError = e.gptError || {
                        code: 'PROVIDER_ERROR',
                        message: e.message || 'Ошибка генерации',
                    };
                });
            }
        } finally {
            runInAction(() => {
                this.streaming = false;
                this.abortController = null;
            });
            this.loadCredits();
        }
    }

    async generateImage(prompt, params = {}) {
        const model = this.settings.selected_image_model;
        if (!model) {
            this.streamError = { code: 'MODEL_NOT_FOUND', message: 'Выберите модель изображений' };
            return;
        }
        if (this.streaming) return;

        let chatId = this.activeChatId;
        if (!chatId) {
            const chat = await this.newChat();
            chatId = chat.id;
        }

        const readyRefs = this.attachments.filter(
            (a) => a.status === 'ready' && a.fileId && (a.kind === 'image' || (a.mime || '').startsWith('image/'))
        );

        const aspectRatio =
            params.aspect_ratio ||
            (params.width && params.height ? `${params.width}:${params.height}` : '1:1');
        const count = Math.max(1, Number(params.n) || 1);
        const now = new Date().toISOString();
        const tempUserId = `temp-user-${Date.now()}`;
        const tempAsstId = `temp-asst-${Date.now()}`;

        const tempUser = {
            id: tempUserId,
            chat_id: chatId,
            role: 'user',
            content: prompt,
            attachments: readyRefs.map((f) => ({
                file_id: f.fileId,
                kind: f.kind || 'image',
                name: f.name,
                mime: f.mime,
                size: f.size,
                url: f.url,
            })),
            status: 'complete',
            created_at: now,
            updated_at: now,
        };
        const tempAssistant = {
            id: tempAsstId,
            chat_id: chatId,
            role: 'assistant',
            content: '',
            status: 'generating_image',
            model,
            aspect_ratio: aspectRatio,
            source_prompt: prompt,
            source_attachments: tempUser.attachments,
            pending_images: Array.from({ length: count }, (_, i) => ({
                key: `${tempAsstId}-${i}`,
                aspect_ratio: aspectRatio,
                width: params.width,
                height: params.height,
            })),
            generated_images: [],
            created_at: now,
            updated_at: now,
        };

        runInAction(() => {
            this.messages = [...this.messages, tempUser, tempAssistant];
            this.streaming = true;
            this.streamError = null;
            this.stickToBottom = true;
            this.mode = 'image';
            this.imageDraft = '';
            this.attachments = this.attachments.filter((a) => a.status !== 'ready');
        });

        try {
            const { width, height, ...rest } = params;
            const apiPayload = {
                chat_id: chatId,
                model,
                prompt,
                reference_file_ids: readyRefs.map((f) => f.fileId),
                ...rest,
            };
            // width/height — только вместе с явным size (кастомные пиксели)
            if (params.size) {
                if (width) apiPayload.width = width;
                if (height) apiPayload.height = height;
            }
            await api.generateImage(apiPayload);
            await this.loadChats();
            await this.selectChat(chatId);
            try {
                await this.loadSettings();
            } catch (_) {
                /* статистика стоимости — необязательна */
            }
        } catch (e) {
            runInAction(() => {
                this.streamError = e.gptError || {
                    code: 'PROVIDER_ERROR',
                    message: e.message || 'Ошибка генерации изображения',
                };
                this.messages = this.messages.map((m) =>
                    m.id === tempAsstId
                        ? {
                              ...m,
                              status: 'error',
                              error: this.streamError,
                              pending_images: [],
                          }
                        : m
                );
            });
        } finally {
            runInAction(() => {
                this.streaming = false;
            });
            this.loadCredits();
        }
    }

    async generateSpeech(text) {
        const model = this.settings.selected_audio_model;
        if (!model) {
            this.streamError = { code: 'MODEL_NOT_FOUND', message: 'Выберите аудиомодель' };
            return;
        }
        if (this.streaming) return;
        let chatId = this.activeChatId;
        if (!chatId) {
            const chat = await this.newChat();
            chatId = chat.id;
        }
        const now = new Date().toISOString();
        const tempUserId = `temp-user-${Date.now()}`;
        const tempAsstId = `temp-asst-${Date.now()}`;
        runInAction(() => {
            this.messages = [
                ...this.messages,
                {
                    id: tempUserId,
                    chat_id: chatId,
                    role: 'user',
                    content: text,
                    attachments: [],
                    status: 'complete',
                    created_at: now,
                    updated_at: now,
                },
                {
                    id: tempAsstId,
                    chat_id: chatId,
                    role: 'assistant',
                    content: '',
                    attachments: [],
                    status: 'generating_audio',
                    model,
                    created_at: now,
                    updated_at: now,
                },
            ];
            this.streaming = true;
            this.streamError = null;
            this.stickToBottom = true;
            this.mode = 'audio';
            this.audioDraft = '';
        });
        try {
            const modelInfo = this.audioModels.find((m) => m.id === model);
            const params = modelInfo?.supported_parameters || {};
            const voices = paramOptions(params, 'voices');
            const formats = [
                ...paramOptions(params, 'response_format'),
                ...paramOptions(params, 'response_formats'),
            ];
            const payload = {
                chat_id: chatId,
                model,
                input: text,
            };
            if (voices.some((item) => item.value === this.audioParams.voice)) {
                payload.voice = this.audioParams.voice;
            }
            if (formats.some((item) => item.value === this.audioParams.response_format)) {
                payload.response_format = this.audioParams.response_format;
            }
            await api.generateSpeech(payload);
            await this.loadChats();
            await this.selectChat(chatId);
        } catch (e) {
            runInAction(() => {
                this.streamError = e.gptError || {
                    code: 'PROVIDER_ERROR',
                    message: e.message || 'Ошибка озвучки',
                };
                this.messages = this.messages.map((m) =>
                    m.id === tempAsstId ? { ...m, status: 'error', error: this.streamError } : m
                );
            });
        } finally {
            runInAction(() => {
                this.streaming = false;
            });
            this.loadCredits();
        }
    }

    async transcribeAudio() {
        const model = this.settings.selected_audio_model;
        const ready = this.attachments.find(
            (a) =>
                a.status === 'ready' &&
                a.fileId &&
                ((a.kind || '').includes('audio') || (a.mime || '').startsWith('audio/'))
        );
        if (!model) {
            this.streamError = { code: 'MODEL_NOT_FOUND', message: 'Выберите модель распознавания' };
            return;
        }
        if (!ready) {
            this.streamError = { code: 'UPLOAD_ERROR', message: 'Прикрепите аудиофайл' };
            return;
        }
        if (this.streaming) return;
        let chatId = this.activeChatId;
        if (!chatId) {
            const chat = await this.newChat();
            chatId = chat.id;
        }
        runInAction(() => {
            this.streaming = true;
            this.streamError = null;
            this.mode = 'audio';
        });
        try {
            await api.transcribeAudio({
                chat_id: chatId,
                model,
                file_id: ready.fileId,
            });
            runInAction(() => {
                this.attachments = this.attachments.filter((a) => a.fileId !== ready.fileId);
            });
            await this.loadChats();
            await this.selectChat(chatId);
        } catch (e) {
            runInAction(() => {
                this.streamError = e.gptError || {
                    code: 'PROVIDER_ERROR',
                    message: e.message || 'Ошибка расшифровки',
                };
            });
        } finally {
            runInAction(() => {
                this.streaming = false;
            });
            this.loadCredits();
        }
    }

    async generateVideo(prompt) {
        const model = this.settings.selected_video_model;
        if (!model) {
            this.streamError = { code: 'MODEL_NOT_FOUND', message: 'Выберите модель видео' };
            return;
        }
        if (this.streaming) return;
        let chatId = this.activeChatId;
        if (!chatId) {
            const chat = await this.newChat();
            chatId = chat.id;
        }
        const readyRefs = this.attachments.filter(
            (a) => a.status === 'ready' && a.fileId && (a.kind === 'image' || (a.mime || '').startsWith('image/'))
        );
        runInAction(() => {
            this.streaming = true;
            this.streamError = null;
            this.mode = 'video';
            this.videoDraft = '';
            this.attachments = this.attachments.filter((a) => a.status !== 'ready');
        });
        try {
            const created = await api.generateVideo({
                chat_id: chatId,
                model,
                prompt,
                duration: this.videoParams.duration || undefined,
                aspect_ratio: this.videoParams.aspect_ratio || undefined,
                resolution: this.videoParams.resolution || undefined,
                generate_audio: this.videoParams.generate_audio || undefined,
                reference_file_ids: readyRefs.map((f) => f.fileId),
            });
            await this.loadChats();
            await this.selectChat(chatId);
            if (created?.id && created.status === 'generating_video') {
                this.pollVideoStatus(created.id, chatId);
            }
        } catch (e) {
            runInAction(() => {
                this.streamError = e.gptError || {
                    code: 'PROVIDER_ERROR',
                    message: e.message || 'Ошибка генерации видео',
                };
            });
        } finally {
            runInAction(() => {
                this.streaming = false;
            });
            this.loadCredits();
        }
    }

    async pollVideoStatus(messageId, chatId) {
        if (!messageId || this._videoPollIds.has(messageId)) return;
        this._videoPollIds.add(messageId);
        try {
            for (let i = 0; i < 90; i += 1) {
                await new Promise((resolve) => setTimeout(resolve, 4000));
                try {
                    const data = await api.fetchVideoStatus(messageId);
                    if (this.activeChatId === chatId && data) {
                        runInAction(() => {
                            this.messages = this.messages.map((m) => (m.id === messageId ? data : m));
                        });
                    }
                    if (data?.status === 'complete' || data?.status === 'error') {
                        if (this.activeChatId === chatId) await this.selectChat(chatId);
                        if (data.status === 'error') {
                            runInAction(() => {
                                this.streamError = data.error || {
                                    code: 'PROVIDER_ERROR',
                                    message: 'Генерация видео не удалась',
                                };
                            });
                        }
                        return;
                    }
                } catch (e) {
                    runInAction(() => {
                        this.streamError = e.gptError || {
                            code: 'PROVIDER_ERROR',
                            message: e.message || 'Не удалось проверить статус видео',
                        };
                    });
                    return;
                }
            }
            runInAction(() => {
                this.streamError = {
                    code: 'TIMEOUT',
                    message: 'Видео ещё генерируется. Обновите чат позже.',
                };
            });
        } finally {
            this._videoPollIds.delete(messageId);
        }
    }
}
const chatStore = new ChatStore();
export default chatStore;
